import { useEffect, useMemo, useState } from 'react';
import { hrApi } from '@/api/hr.api';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import type { SelectOption } from '@/components/ui/Select';
import type { HrShiftType } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { useSelectLabels } from '@/hooks/useSelectLabels';
import { useTranslation } from 'react-i18next';

export function ShiftTypeSelect({
  label = 'Shift type',
  value = '',
  onChange,
  placeholder,
  className,
  required,
}: {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
}) {
  const selectLabels = useSelectLabels();
  const { t } = useTranslation('hr');
  const [types, setTypes] = useState<HrShiftType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hrApi
      .listShiftTypes()
      .then(setTypes)
      .catch((err) => showApiError(err, t('errors.failedToLoadShiftTypes')))
      .finally(() => setLoading(false));
  }, []);

  const options: SelectOption[] = useMemo(
    () =>
      types
        .filter((type) => type.active !== false)
        .map((type) => ({
          value: type.code,
          label: `${type.name} (${type.code})`,
        })),
    [types],
  );

  return (
    <SearchableSelect
      className={className}
      label={label}
      required={required}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder ?? selectLabels.selectShiftType}
      loading={loading}
      searchPlaceholder={selectLabels.searchShiftTypes}
      emptyMessage={selectLabels.noShiftTypes}
    />
  );
}
